import { Contractor } from "@components/modules/contractors/ContractorModel";
import { Store as ContractorStore } from "@components/modules/contractors/ModalContractor/store";
import { RelatedDocumentsEditModalStore } from "@components/modules/expenses/Create/components/related-documents/related-documents-edit-modal/store";
import type { IOption } from "@components/ui-kit";
import { t } from "@lingui/macro";
import {
  BusinessFormEnum,
  CheckSequenceNumberExistDocument,
  type CheckSequenceNumberExistQuery,
  type CheckSequenceNumberExistQueryVariables,
  ContractorDetailDocument,
  type ContractorDetailQuery,
  type ContractorDetailQueryVariables,
  ContractorForCreateExpenseDocument,
  type ContractorForCreateExpenseQuery,
  type ContractorForCreateExpenseQueryVariables,
  type ContractsSimpleQuery,
  type ContractsSimpleQueryVariables,
  ContractTypeEnum,
  ConvertAmountDocument,
  type ConvertAmountQuery,
  type ConvertAmountQueryVariables,
  type CostCenterFragment,
  type CreateExpenseInput,
  CreateExpenseItemDescriptionDocument,
  type CreateExpenseItemDescriptionMutation,
  type CreateExpenseItemDescriptionMutationVariables,
  type Currency,
  DeleteExpenseDocument,
  DeleteExpenseItemDescriptionDocument,
  type DeleteExpenseItemDescriptionMutation,
  type DeleteExpenseItemDescriptionMutationVariables,
  type DeleteExpenseMutation,
  type DeleteExpenseMutationVariables,
  type DependenciesForCreateExpenseQuery,
  type DependenciesForCreateExpenseQueryVariables,
  ExchangeRateSource,
  type ExpenseItemDesciption,
  ExpensePaymentTypeEnum,
  ExpenseTypeEnum,
  GetExchangeRateDocument,
  type GetExchangeRateQuery,
  type GetExchangeRateQueryVariables,
  GetExpensePredictionDocument,
  type GetExpensePredictionQuery,
  type GetExpensePredictionQueryVariables,
  GetNewSequenceNumberDocument,
  type GetNewSequenceNumberQuery,
  type GetNewSequenceNumberQueryVariables,
  ModelLockLockableTypeEnum,
  SequenceTypesEnum,
  type UpdateExpenseInput,
  UpdateExpenseItemDescriptionDocument,
  type UpdateExpenseItemDescriptionMutation,
  type UpdateExpenseItemDescriptionMutationVariables,
} from "@src/__generated__/graphql";
import {
  type CompanyData,
  ExpenseBudgetItemTypeEnum,
  type LockDocument,
  LockDocumentDocument,
  type LockDocumentMutationVariables,
  LockDocumentTypeEnum,
  type PaymentType,
} from "@src/__generated__/urql-graphql";
import { ExpenseCreditAccountsCardStore } from "@src/components/modules/expenses/Create/components/credit-accounts-card/store";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { client } from "@src/services/client";
import type { AppStore } from "@src/stores/AppStore";
import { EXCHANGE_RATE_DENOMINATION } from "@src/stores/forms/SettingsModalState/FinancialSettingsState";
import type { TRefetch } from "@src/utils/apolloHelpers";
import { MediaModel } from "@src/utils/components/dropzone";
import { can } from "@src/utils/components/permissions";
import { convertToWorkspaceCurrency } from "@src/utils/currency";
import { toApiDate } from "@src/utils/dates";
import { denominate, nominate, percent } from "@src/utils/formatters";
import { required } from "@src/utils/forms/validators";
import mapToOptions from "@src/utils/map-to-options";
import { toCostCentersOptions } from "@src/utils/map-to-options/cost-centers";
import { toTagOptions } from "@src/utils/map-to-options/tags";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { NumberModel } from "@src/utils/models";
import { getQueryParamOptional } from "@src/utils/routing";
import { secondsToHours } from "@src/utils/time";
import { type DateRange, FormMode } from "@src/utils/types";
import type { TaskOptionLabelFormatterArgs } from "@src/widgets/TaskSelect/TaskSelect";
import { FieldState, FormState } from "formstate";
import { GraphQLError } from "graphql";
import { compact, omit, orderBy, pick } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { now } from "mobx-utils";
import Router from "next/router";
import { Form } from "../form";
import type {
  TAccountingAccountsSimple,
  TAddExpenseItemArgs,
  TClientsSimple,
  TContract,
  TCurrencies,
  TExpense,
  TFondContractorsSimple,
  TRelatedExpense,
  TVatRates,
} from "../models";

type PaymentTypeOption = {
  system_name: PaymentType["system_name"];
  label: PaymentType["name"];
  value: PaymentType["id"];
};

type IKeys =
  | "type"
  | "contract_id"
  | "contractor_id"
  | "issue_date"
  | "date_of_supply"
  | "due_date"
  | "expense_vat_items"
  | "symbols"
  | "two_percent_fund"
  | "set_accounting"
  | "workspace_company_id"
  | "workspace_bank_account_id"
  | "expense_no";

export class Store {
  appStore: AppStore;
  editContractorStore: ContractorStore;
  form: Form;
  creditAccountsCardStore: ExpenseCreditAccountsCardStore;

  emailModalState = new DisclosureState();

  typesOptions = [
    { label: t`Bill`, value: ExpenseTypeEnum.Bill },
    { label: t`Other`, value: ExpenseTypeEnum.Other },
    { label: t`Invoice`, value: ExpenseTypeEnum.Invoice },
    { label: t`Contract`, value: ExpenseTypeEnum.Contract },
    { label: t`Proforma`, value: ExpenseTypeEnum.Proforma },
    { label: t`Card payments`, value: ExpenseTypeEnum.Card },
    { label: t`Payment Receipt`, value: ExpenseTypeEnum.PaymentReceipt },
  ];

  @observable.ref
  paymentTypeOptions: PaymentTypeOption[] = [];

  @observable.ref vatRates: TVatRates = [];
  @observable.ref currencies: TCurrencies = [];
  @observable.ref clients: TClientsSimple = [];
  @observable.ref accountingAccounts: TAccountingAccountsSimple = [];
  @observable.ref fondContractors: TFondContractorsSimple = [];
  @observable.ref costCenters: CostCenterFragment[] = [];
  @observable.ref itemDescriptions: ExpenseItemDesciption[] = [];

  refetchDependencies?: TRefetch<
    DependenciesForCreateExpenseQueryVariables,
    DependenciesForCreateExpenseQuery
  >;

  @observable.ref contractsOptions: IOption[] = [];
  refetchContractsOptions?: TRefetch<
    ContractsSimpleQueryVariables,
    ContractsSimpleQuery
  >;

  @observable.ref expense?: TExpense;
  @observable.ref expenseItemStructured?: TExpense["expenseItemsStructured"];

  @observable.ref
  expenseDocuments: TExpense["expense_documents"] = [];
  @observable.ref
  relatedExpenses: TRelatedExpense[] = [];
  relatedDocumentsModalStore: RelatedDocumentsEditModalStore;

  @observable private _contractor?: Contractor;
  @observable fetchingContractor = false;
  @observable activeContractorOption?: IOption = undefined;

  @observable.ref private _contract: TContract;
  @observable contractLoading = false;
  refetchContract?: TRefetch<
    ContractorForCreateExpenseQueryVariables,
    ContractorForCreateExpenseQuery
  >;

  @observable scrollTo = {
    basicInfoCard: false,
    expenseDataCard_generalInfo: false,
    expenseDataCard_vatItems: false,
    expenseDataCard_totalAmount: false,
    expenseDataCard_paymentInfo: false,
  };

  @observable fileCardActiveTab: 0 | 1 = 0;
  @observable fileContractor: Partial<CompanyData> = {};
  @observable predictionFiles: [File | undefined, File | undefined] = [
    undefined,
    undefined,
  ];
  @observable
  ocrPredictions: [
    GetExpensePredictionQuery["fetchOCRExpenseData"] | undefined,
    GetExpensePredictionQuery["fetchOCRExpenseData"] | undefined,
  ] = [undefined, undefined];

  @observable predictionFormCheck = true;
  @observable predictionBudgetItemsCheck = false;
  @observable predictionFilledForm: [boolean, boolean] = [false, false];
  @observable predictionFilledBudgetItems: [boolean, boolean] = [false, false];
  @observable predictionFillingForm = false;
  @observable predictionFillingBudgetItems = false;

  @observable.ref mode: FormMode;
  @observable.ref lockedDocument: TExpense["lockedDocument"] = null;
  @observable.ref modelLock: TExpense["modelLock"] = null;
  @observable.ref expenseUpdatedWhileInactive = false;
  @observable convertedAmount: number | undefined;
  @observable.ref plannedExpenseMappingsError?: string;
  exchangeRateFetcher = new FetchHelper<
    GetExchangeRateQuery,
    GetExchangeRateQueryVariables
  >(GetExchangeRateDocument);
  convertAmountFetcher = new FetchHelper<
    ConvertAmountQuery,
    ConvertAmountQueryVariables
  >(ConvertAmountDocument);

  expenseItemDescriptionModalState = new DisclosureState({
    onClose: () => {
      this.editingDescriptionIds.clear();
      this.updatingDescriptionIds.clear();
      this.newDescriptionField.reset();
    },
  });
  private expenseItemDescriptionCreateMutator = new MutationHelper<
    CreateExpenseItemDescriptionMutation,
    CreateExpenseItemDescriptionMutationVariables
  >(CreateExpenseItemDescriptionDocument);
  private expenseItemDescriptionUpdateMutator = new MutationHelper<
    UpdateExpenseItemDescriptionMutation,
    UpdateExpenseItemDescriptionMutationVariables
  >(UpdateExpenseItemDescriptionDocument);
  private expenseItemDescriptionDeleteMutator = new MutationHelper<
    DeleteExpenseItemDescriptionMutation,
    DeleteExpenseItemDescriptionMutationVariables
  >(DeleteExpenseItemDescriptionDocument);

  @observable editingDescriptionIds = new Set<string>();
  @observable updatingDescriptionIds = new Set<string>();
  @observable newDescriptionField = new FieldState<string>("").validators(
    required,
  );

  private expenseNoCheckFetcher = new FetchHelper<
    CheckSequenceNumberExistQuery,
    CheckSequenceNumberExistQueryVariables
  >(CheckSequenceNumberExistDocument);

  @observable.ref expenseNoOptions: IOption[] = [];
  private expenseNoFetcher = new FetchHelper<
    GetNewSequenceNumberQuery,
    GetNewSequenceNumberQueryVariables
  >(GetNewSequenceNumberDocument);

  private predictionFetcher = new FetchHelper<
    GetExpensePredictionQuery,
    GetExpensePredictionQueryVariables
  >(GetExpensePredictionDocument);

  private contractorFetcher = new FetchHelper<
    ContractorDetailQuery,
    ContractorDetailQueryVariables
  >(ContractorDetailDocument);

  private contractorForCreateExpenseFetcher = new FetchHelper<
    ContractorForCreateExpenseQuery,
    ContractorForCreateExpenseQueryVariables
  >(ContractorForCreateExpenseDocument);

  private deleteExpenseMutator = new MutationHelper<
    DeleteExpenseMutation,
    DeleteExpenseMutationVariables
  >(DeleteExpenseDocument);

  deleteExpenseModalState = new DisclosureState();

  isBudgetItemLoadingByCode = new BooleanState();

  constructor(appStore: AppStore, mode: FormMode) {
    makeObservable(this);
    this.mode = mode;
    this.createVatItem = this.createVatItem.bind(this);
    this.createBudgetItem = this.createBudgetItem.bind(this);

    this.appStore = appStore;
    this.editContractorStore = new ContractorStore();
    this.creditAccountsCardStore = new ExpenseCreditAccountsCardStore(this);
    this.form = new Form(this);
    this.relatedDocumentsModalStore = new RelatedDocumentsEditModalStore(this);
    this.initForm();
    appStore.tagStore.fetchTags();

    if (mode !== FormMode.CREATE) return;
    const contractorId = getQueryParamOptional("contractorId", Router.query);
    if (!contractorId) return;
    this.form.contractor_id.onChange(contractorId);
    this.fetchContractor(contractorId);
  }

  initForm() {
    this.form.currency_id.reset(
      this.appStore.workspaceStore.settings?.currency.id,
    );
    this.form.total_amount_with_vat_in_currency.reset(
      this.form.total_amount_with_vat_in_currency.value
        .setDenominator(
          this.appStore.workspaceStore.settings?.currency.denomination!,
        )
        .setValue(0),
    );
    this.form.exchange_rate.reset();
    if (this.mode !== FormMode.CREATE) return;

    this.form.addExpenseItem(
      new NumberModel(
        0,
        this.appStore.workspaceStore.settings?.currency.denomination ?? 2,
      ),
      {
        vatRate: this.appStore.workspaceStore.settings?.defaultVatRate,
      },
    );
  }

  hasBrand(brandId: string) {
    return this.expenseItemStructured?.find(
      (item) => item.brand?.id === brandId,
    );
  }

  @computed get allBudgetItems() {
    return Array.from(this.form.expense_items.values());
  }

  @action.bound
  handleRemoveBudgetItem(id: string) {
    this.form.expense_items.delete(id);
    this.clearPlannedExpenseMappingsError();
  }

  @computed get showVatSelect() {
    return (
      this.appStore.workspaceStore.settings?.show_expense_item_vat_rate ?? false
    );
  }

  @computed get showCostRatio() {
    return (
      this.appStore.workspaceStore.settings
        ?.expense_effectix_custom_cost_ratio ?? false
    );
  }

  @computed get hasSomeTrackHours() {
    return this.allBudgetItems.some((item) => item.showTrackHours);
  }

  @computed get hasSomeStatusIcon() {
    return this.allBudgetItems.some((item) => item.showStatusIcon);
  }

  @computed get hasSomeItemClosed() {
    return this.allBudgetItems.some((item) => item.isItemClosed);
  }

  @computed get budgetItemsColumns() {
    /* eslint-disable lingui/no-unlocalized-strings */
    const frCount = this.showCostRatio ? 6 : 5;
    const columns = compact([
      `repeat(${frCount}, 0.2fr) 60px`,
      this.hasSomeTrackHours ? "80px" : null,
      this.hasSomeStatusIcon ? "14px" : null,
      this.hasSomeItemClosed ? "20px" : null,
      "70px",
    ]);
    /* eslint-enable lingui/no-unlocalized-strings */

    return columns.join(" ");
  }

  @computed get budgetPathSelectColSpan() {
    let colSpan = 3;

    if (this.showCostRatio) {
      colSpan += 1;
    }

    return colSpan;
  }

  @computed get tagSelectColSpan() {
    let span = 3;

    if (
      this.allBudgetItems.every(
        (item) =>
          !item.showStatusIcon && !item.showTrackHours && !item.isItemClosed,
      )
    ) {
      span = 2;
    }

    return span;
  }

  @computed get selectedVatRates() {
    return new Set(
      Array.from(this.form.expense_vat_items.values()).map(
        ({ vat_percent_informative }) => vat_percent_informative.value,
      ),
    );
  }

  @computed get vatRatesOptions() {
    return this.vatRates
      .filter(({ deleted }) => !deleted)
      .map((i) => ({
        label: percent.format(i.amount),
        value: i.amount.toString(),
      }));
  }

  @computed get vatRatesOptionsForBudgetItem(): IOption[] {
    return this.vatRatesOptions.filter(({ value }) =>
      this.selectedVatRates.has(Number(value)),
    );
  }

  @computed get currenciesOptions() {
    return this.currencies.map((i) => ({
      label: i.code,
      value: i.id,
    }));
  }

  @computed get brandsOptions() {
    return orderBy(
      this.clients.flatMap((client) =>
        client.brands.map((i) => ({
          label: `${i.code} - ${i.name}`,
          value: i.id,
        })),
      ),
      ["label"],
      ["asc"],
    );
  }

  @computed get accountingAccountsOptions() {
    return this.accountingAccounts.map((i) => ({
      label: `${i.account_number} - ${i.title}`,
      value: i.id,
    }));
  }

  @computed get fondContractorsOptions() {
    return this.fondContractors.map((i) => ({
      label: i.legal_name,
      value: i.id,
    }));
  }

  @computed get costCentersOptions() {
    return toCostCentersOptions(this.costCenters);
  }

  @computed get contractFiles(): MediaModel[] {
    if (!this.contract?.files) return [];
    return this.contract.files.flatMap((i) => (i ? [new MediaModel(i)] : []));
  }

  @computed get selectedCurrency(): Currency | undefined {
    return this.currencies.find((i) => i.id === this.form.currency_id.value);
  }

  @computed get tagOptions() {
    return toTagOptions(this.appStore.tagStore.tags);
  }

  @computed get isExpenseDraft(): boolean | undefined {
    return this.expense?.is_draft;
  }

  @computed get isUsedDifferentCurrency(): boolean {
    return (
      this.form.currency_id.value !==
      this.appStore.workspaceStore.settings?.currency.id
    );
  }

  @computed get showRelatedDocuments(): boolean {
    const type = this.form.type.value;
    return (
      type === ExpenseTypeEnum.PaymentReceipt ||
      type === ExpenseTypeEnum.Proforma ||
      type === ExpenseTypeEnum.Invoice
    );
  }

  @computed get totalAmountOfVatItems(): number {
    let sum = 0;

    this.form.expense_vat_items.forEach((item) => {
      sum +=
        item.amount_in_currency.value.denominatedValue +
        item.vat_in_currency.value;
    });

    return sum;
  }

  @computed get totalAmountOfVatItemsWithoutVat(): number {
    let sum = 0;
    this.form.expense_vat_items.forEach((i) => {
      sum += i.amount_in_currency.value.denominatedValue;
    });

    return sum;
  }

  @computed get roundingAmount(): number {
    return this.totalAmount - this.totalAmountOfVatItems;
  }

  get totalAmount(): number {
    return this.form.total_amount_with_vat_in_currency.value.denominatedValue;
  }

  toWorkspaceNumberModel(amount: number): NumberModel {
    const workspaceDenomination =
      this.appStore.workspaceStore.settings?.currency.denomination ?? 2;
    const exchangeRate = denominate(
      this.form.exchange_rate.value ?? 1,
      EXCHANGE_RATE_DENOMINATION,
    );

    const convertedAmount = this.isUsedDifferentCurrency
      ? convertToWorkspaceCurrency(amount, {
          exchange_rate: exchangeRate,
        })
      : amount;

    return NumberModel.fromDenomValue(
      Math.round(convertedAmount),
      workspaceDenomination,
    );
  }

  @computed get totalAmountForExpense(): number {
    return this.form.total_amount_includes_vat.value
      ? this.totalAmountOfVatItems
      : this.totalAmountOfVatItemsWithoutVat;
  }

  @computed get totalAmountOfBudgetItems(): number {
    let sum = 0;
    this.form.expense_items.forEach((i) => {
      sum += i.form.$.amount_in_currency.value.denominatedValue;
    });

    for (const creditAccount of this.creditAccountsCardStore.items) {
      sum += denominate(
        Number(creditAccount.$.amount_in_currency.value),
        this.selectedCurrency?.denomination ?? 2,
      );
    }

    return sum;
  }

  @computed get totalVatOfVatItems(): number {
    let sum = 0;

    this.form.expense_vat_items.forEach((item) => {
      sum += item.vat_in_currency.value;
    });

    return sum;
  }

  @computed get totalVatOfBudgetItems(): number {
    let sum = 0;

    this.form.expense_items.forEach((i) => {
      sum += i.form.$.vat_amount_in_currency.value.denominatedValue;
    });

    return sum;
  }

  @computed get vatTotalsMismatch(): boolean {
    if (!this.showVatSelect) return false;

    return this.totalVatOfVatItems !== this.totalVatOfBudgetItems;
  }

  @computed get contractor(): Contractor | undefined {
    if (
      this._contractor &&
      this._contractor?.id === this.form.contractor_id.value
    ) {
      return this._contractor;
    }
    return undefined;
  }

  @computed get isFetchingContractor(): boolean {
    return this.contractorForCreateExpenseFetcher.isLoading.value;
  }

  @computed get contract(): TContract | undefined {
    if (this._contract && this._contract?.id === this.form.contract_id.value) {
      return this._contract;
    }
    return undefined;
  }

  @computed get prediction() {
    return this.ocrPredictions[this.fileCardActiveTab];
  }

  @computed get activePredictionFile() {
    return this.predictionFiles[this.fileCardActiveTab];
  }

  @computed get activePredictionFormFilled() {
    return this.predictionFilledForm[this.fileCardActiveTab];
  }

  @computed get activePredictionBudgetItemsFilled() {
    return this.predictionFilledBudgetItems[this.fileCardActiveTab];
  }

  @computed get everyItemApproved() {
    if (this.form.expense_items.size === 0) return true;
    return Array.from(this.form.expense_items.values()).every(
      (itemStore) =>
        itemStore.form.$.approved.value && itemStore.hasCompleteBudgetPath,
    );
  }

  @computed get isDocumentLockedByMe(): boolean {
    return this.lockedDocument?.user.id === this.appStore.authStore.user?.id;
  }

  @computed get isDocumentLocked(): boolean {
    return !!this.lockedDocument && !this.isDocumentLockedByMe;
  }

  @computed get hasModelLock(): boolean {
    return !!this.modelLock && this.modelLock.is_locked;
  }

  @computed get isLinkedToLockedTimeBilling(): boolean {
    return (
      this.expense?.expenseItemsStructured.some((structuredItem) =>
        structuredItem.expense_items.some((item) =>
          item.timeBillings?.some((tb) => tb.is_locked),
        ),
      ) ?? false
    );
  }

  @computed
  get canDeleteExpense(): boolean {
    return (
      can("expense_delete_own", "expense_delete_all") && !!this.expense?.id
    );
  }

  @computed get isDeleting(): boolean {
    return this.deleteExpenseMutator.isLoading.value;
  }

  @action.bound
  async deleteExpense(): Promise<boolean> {
    if (!this.expense?.id) return false;

    const [data, error] = await this.deleteExpenseMutator.mutate({
      id: this.expense.id,
    });

    if (error || !data?.deleteExpense) {
      return false;
    }

    this.appStore.UIStore.toast({
      title: t`The expense was deleted.`,
      status: "success",
    });

    this.deleteExpenseModalState.onClose();
    return true;
  }

  get contractorOptionalValidateFields() {
    const selectedPaymentOption = this.paymentTypeOptions.find(
      (option) => option.value === this.form.payment_type_id.value,
    )?.system_name;

    const ibanRequired =
      this.form.already_paid.value &&
      !!selectedPaymentOption &&
      selectedPaymentOption === ExpensePaymentTypeEnum.BankTransfer;

    return {
      iban: ibanRequired,
      swift:
        ibanRequired &&
        this.editContractorStore.form.country_id.value !==
          this.appStore.workspaceStore.settings?.country.id,
    };
  }

  /**
   * Time difference from locked_to as epoch
   */
  @computed get timeDifference() {
    if (!this.lockedDocument?.locked_to) return null;
    return new Date(new Date(this.lockedDocument.locked_to).getTime() - now());
  }

  @computed
  get relatedExpensesCount(): number {
    return this.relatedExpenses.length;
  }

  @action.bound
  clearPlannedExpenseMappingsError(): void {
    this.plannedExpenseMappingsError = undefined;
  }

  @action.bound
  validatePlannedExpenseMappings(): boolean {
    const hasInvalidMappings = this.allBudgetItems.some(
      (item) => !item.plannedExpenseMappingStore.hasValidPersistedMappings,
    );

    if (!hasInvalidMappings) {
      this.clearPlannedExpenseMappingsError();
      return true;
    }

    this.plannedExpenseMappingsError = t`Planned expenses mapping must match the expense item amount before saving.`;
    return false;
  }

  @action.bound
  removeRelatedExpense(expenseId: string): void {
    this.relatedExpenses = this.relatedExpenses.filter(
      (e) => e.id !== expenseId,
    );
    this.form.related_expense_ids.onChange(
      this.form.related_expense_ids.value.filter((id) => id !== expenseId),
    );
  }

  @action.bound
  resetRelatedDocuments(): void {
    this.relatedExpenses = [];
    this.form.related_expense_ids.onChange([]);
  }

  @action setDependencies(src: DependenciesForCreateExpenseQuery) {
    this.paymentTypeOptions = src.paymentTypes.map((i) => ({
      label: i.name,
      value: i.id,
      system_name: i.system_name,
    }));
    this.vatRates = src.vatRates || [];
    this.currencies = src.currencies || [];
    this.clients = src.clientsSimpleMap;
    this.accountingAccounts = src.accountingAccountsSimpleMap;
    this.fondContractors = src.fondContractorsSimpleMap;
    this.costCenters = src.accountingCentres;
    this.itemDescriptions =
      src.expenseItemDescriptions?.filter((description) => !!description) ?? [];
  }

  @action.bound
  setExpense(expense: TExpense) {
    this.expense = expense;
    this.expenseItemStructured = expense.expenseItemsStructured;
    this.lockedDocument = expense.lockedDocument;
    this.modelLock = expense.modelLock;

    this.fillForm(expense);
    this.creditAccountsCardStore.addDefaultCreditAccountOptions(
      expense.expenseItemsStructured
        .flatMap((structuredItem) => structuredItem.expense_items)
        .filter(({ globalCreditAccount }) => Boolean(globalCreditAccount))
        .flatMap(({ globalCreditAccount }) =>
          mapToOptions.globalCreditAccounts(
            globalCreditAccount ? [globalCreditAccount] : [],
          ),
        ),
    );

    if (expense.contractor) {
      this.setContractor(
        new Contractor({
          id: expense.contractor.id,
          legal_name: expense.legal_name ?? "",
          ...pick(expense, [
            "street",
            "city",
            "country",
            "zip",
            "tax_no",
            "vat_no",
            "registration_no",
            "national_id",
            "swift",
            "iban",
            "business_form",
            "defaultAccountingAccount",
            "bank_account",
            "bank_account_code",
          ]),
        }),
        true,
      );
    }

    if (
      !expense.lockedDocument ||
      this.appStore.authStore.user?.id === expense.lockedDocument.user.id
    ) {
      this.lockDocument(expense.id);
    }
  }

  @action.bound async lockDocument(
    doc_id: LockDocumentMutationVariables["document_id"],
  ) {
    await client
      .mutation<LockDocument, LockDocumentMutationVariables>(
        LockDocumentDocument,
        {
          document_id: doc_id,
          document_type: LockDocumentTypeEnum.Expense,
        },
      )
      .toPromise();
  }

  async fillForm(expense: TExpense) {
    const { form } = this;
    form.expense_items.clear();
    form.contractor_id.onChange(expense.contractor?.id ?? "");
    form.contract_id.onChange(expense.contract?.id ?? "");
    form.type.onChange(expense.type);
    form.userCreatedDate.onChange(new Date(expense.user_created_date));
    if (expense.issue_date) {
      form.issue_date.onChange(new Date(expense.issue_date));
    }
    if (expense.date_of_supply) {
      form.date_of_supply.onChange(new Date(expense.date_of_supply));
    }
    if (expense.due_date) {
      form.due_date.onChange(new Date(expense.due_date));
    }
    form.currency_id.onChange(expense.currency.id);
    form.constant_symbol.onChange(expense.constant_symbol ?? "");
    form.specific_symbol.onChange(expense.specific_symbol ?? "");
    form.variable_symbol.onChange(expense.variable_symbol ?? "");
    form.workspace_company_id.onChange(expense.workspace_company_id ?? "");
    form.workspace_bank_account_id.onChange(
      expense.workspace_bank_account_id ?? undefined,
    );

    const budgetItems: TExpense["expenseItemsStructured"][0]["expense_items"] =
      [];
    const creditAccounts: TExpense["expenseItemsStructured"][0]["expense_items"] =
      [];

    for (const structuredItem of expense.expenseItemsStructured) {
      for (const item of structuredItem.expense_items) {
        if (!item.globalCreditAccount) {
          budgetItems.push(item);
          continue;
        }

        creditAccounts.push(item);
      }
    }

    budgetItems.forEach((i) => {
      const position = i?.positions?.[0];
      const plannedExpenseUsages =
        i.budgetItem?.__typename === "ExpenseBudgetItem" ||
        i.budgetItem?.__typename === "OurWorkBudgetItem"
          ? i.budgetItem.plannedExpenses.flatMap((plannedExpense) =>
              plannedExpense.usedExpenses
                .filter((usage) => usage.expenseItem.id === i.id)
                .map((usage) => ({
                  budget_item_planned_expense_id: plannedExpense.id,
                  used_amount: usage.used_amount,
                })),
            )
          : undefined;

      form.addExpenseItem(
        new NumberModel(i.amount_in_currency, expense.currency.denomination),
        {
          id: i.id,
          brand_id: i.brand_id ?? undefined,
          project_id: i.project_id ?? undefined,
          accounting_account_id: i.accountingAccount?.id,
          budget_item_id: this.form.createId(
            i.budgetItem?.id ?? "",
            i.budget_item_type,
          ),
          budget_item_type: i.budget_item_type,
          responsible_id: i?.mandatory_id,
          approved: i?.approved ?? false,
          approvals: i?.approvals ?? [],
          report_date: position?.report_date
            ? new Date(position.report_date)
            : undefined,
          tracked_time: position?.tracked_time
            ? secondsToHours(position?.tracked_time)
            : undefined,
          time_tracking_work_type_id:
            position?.time_tracking_work_type_id ?? undefined,
          task_id: position?.task?.id,
          vatRate: i.vatRate ?? undefined,
          vat_amount_in_currency: i.vat_amount_in_currency ?? undefined,
          accounting_centre_id: i.accounting_centre_id ?? undefined,
          accounting_vat_classification_id:
            i.accounting_vat_classification_id ?? undefined,
          tag_ids: i.tag_ids ?? undefined,
          description_id: i.expense_item_description_id ?? undefined,
          custom_cost_ratio: i.custom_cost_ratio ?? undefined,
          planned_expense_usages: plannedExpenseUsages,
          amount_in_percent: i.amount_in_percent ?? undefined,
        },
      );
    });

    this.creditAccountsCardStore.setItems(
      creditAccounts.map((i) => ({
        id: i.id,
        amount_in_currency: nominate(
          i.amount_in_currency,
          expense.currency.denomination,
        ).toString(),
        global_credit_account_id: i.globalCreditAccount?.id,
        global_credit_account_title: undefined,
        accounting_account_id: i.accountingAccount?.id,
        accounting_centre_id: i.accounting_centre_id ?? undefined,
        accounting_vat_classification_id:
          i.accounting_vat_classification_id ?? undefined,
        description_id: i.expense_item_description_id ?? undefined,
        custom_cost_ratio: i.custom_cost_ratio ?? undefined,
      })),
    );

    if (expense.expenseVatItems?.length) {
      form.resetExpenseVatItems();
    }
    expense.expenseVatItems?.map((i) =>
      form.addExpenseVatItem({
        amount_in_currency: new NumberModel(
          i.amount_in_currency || 0,
          expense.currency.denomination,
        ),
        vat_percent_informative: i.vat_percent_informative || 0,
        vat_in_currency: i.vat_in_currency,
      }),
    );

    form.total_amount_with_vat_in_currency.onChange(
      new NumberModel(
        expense.stats.total_amount_with_vat_in_currency,
        expense.currency.denomination,
      ),
    );

    // reset: skip onDidChange → do not overwrite loaded vat_amount_in_currency
    form.total_amount_includes_vat.reset(expense.total_amount_includes_vat);
    form.variable_symbol.onChange(expense.variable_symbol ?? "");
    form.internal_note.onChange(expense.internal_note || "");
    form.note.onChange(expense.note || "");
    form.internal_number.onChange(expense.internal_number ?? "");
    form.document_number.onChange(expense.document_number ?? "");

    if (expense.paid_at) {
      form.already_paid.onChange(true);
    }

    form.payment_type_id.onChange(expense.payment_type?.id ?? undefined);
    form.paid_at.reset(expense.paid_at ? new Date(expense.paid_at) : undefined);

    if (expense.exchange_rate) {
      form.exchange_rate.onChange(
        nominate(
          expense.exchange_rate,
          this.appStore.workspaceStore.settings?.exchange_rate_denomination ??
            EXCHANGE_RATE_DENOMINATION,
        ),
      );
    }

    this.convertedAmount = convertToWorkspaceCurrency(
      this.totalAmountForExpense,
      {
        exchange_rate:
          expense.exchange_rate ?? denominate(1, EXCHANGE_RATE_DENOMINATION),
      },
    );

    if (expense.fond_contractor_id) {
      form.two_percent_fund.onChange(true);
      form.fond_contractor_id.onChange(expense.fond_contractor_id);
    }

    expense.expense_documents.slice(0, 2).forEach((media, idx) => {
      form.files[idx].setExistingFile(media);
    });

    // Cast to access relatedExpenses which will be available after GraphQL regeneration
    const expenseWithRelations = expense as any;
    if (expenseWithRelations.relatedExpenses) {
      form.related_expense_ids.onChange(
        expenseWithRelations.relatedExpenses.map((e: TRelatedExpense) => e.id),
      );
      this.relatedExpenses = expenseWithRelations.relatedExpenses;
    }

    await this.fetchExpenseNo(
      this.canUserAccess("date_of_supply")
        ? expense.date_of_supply
        : expense.user_created_date,
      expense.workspace_company_id,
      this.sequenceType,
    );

    if (expense.expense_no && expense.invoice_order_sequence_id) {
      this.handleSelectInvoiceNo(
        {
          label: expense.expense_no,
          value: expense.invoice_order_sequence_id,
        },
        false,
      );
    }
  }

  @action setContractsOptions(src: ContractsSimpleQuery) {
    this.contractsOptions =
      src.contractsSimpleMap?.map((i) => ({
        label: i.title,
        value: i.id,
      })) || [];
  }

  @action setContractor(contractor: Contractor, fromExpense = false) {
    this._contractor = contractor;
    this.syncContractorWithModal();
    if (!fromExpense) {
      this.presetVatOnVatItems();
    }
  }

  @action.bound
  async fetchContractor(
    contractorId: string,
    disableVatOverwrite = false,
  ): Promise<void> {
    if (!contractorId) return;

    const [data, error] = await this.contractorForCreateExpenseFetcher.fetch({
      id: contractorId,
    });

    if (error || !data?.contractor) return;

    this.setContractor(new Contractor(data.contractor), disableVatOverwrite);
  }

  syncContractorWithModal() {
    this.editContractorStore.form.reset();
    if (this.contractor) {
      this.editContractorStore.form.fill(this.contractor.serialize());
    }
  }

  presetVatOnVatItems() {
    let vat = 0;

    if (this.contractor?.is_vat_payer) {
      vat =
        this.appStore.workspaceStore.settings?.defaultVatRate.amount ??
        Math.max(...this.vatRates.map(({ amount }) => amount));
    }

    this.form.expense_vat_items.forEach((i) => {
      i.vat_percent_informative.onChange(vat);
    });
    this.setTotalAmountWithVatInCurrency();
    this.syncBudgetItemVatDefaults();
  }

  @action setContract(contract: TContract) {
    this._contract = contract;
  }

  @action setContractLoading(loading: boolean) {
    this.contractLoading = loading;
  }

  @action.bound setScrollTo() {
    const basicInfoCard = new FormState([
      this.form.type,
      this.form.contract_id,
      this.form.contractor_id,
      this.form.two_percent_fund,
    ]);

    if (basicInfoCard.hasFieldError) {
      this.scrollTo.basicInfoCard = true;
      return;
    }

    const expenseDataCard_generalInfo = new FormState([
      this.form.issue_date,
      this.form.date_of_supply,
      this.form.due_date,
      this.form.currency_id,
    ]);

    if (expenseDataCard_generalInfo.hasFieldError) {
      this.scrollTo.expenseDataCard_generalInfo = true;
      return;
    }

    const expenseDataCard_vatItems = this.form.expense_vat_items;

    for (const i of expenseDataCard_vatItems.values()) {
      if (i.$.hasError) {
        this.scrollTo.expenseDataCard_vatItems = true;
        return;
      }
    }

    const expenseDataCard_totalAmount = new FormState([
      this.form.total_amount_with_vat_in_currency,
      this.form.note,
      this.form.variable_symbol,
      this.form.document_number,
    ]);

    if (expenseDataCard_totalAmount.hasError) {
      this.scrollTo.expenseDataCard_totalAmount = true;
      return;
    }

    const expenseDataCard_paymentInfo = new FormState([
      this.form.payment_type_id,
      this.form.paid_at,
    ]);

    if (expenseDataCard_paymentInfo.hasFieldError) {
      this.scrollTo.expenseDataCard_paymentInfo = true;
      return;
    }
  }

  @action resetScrollTo() {
    this.scrollTo = {
      basicInfoCard: false,
      expenseDataCard_generalInfo: false,
      expenseDataCard_vatItems: false,
      expenseDataCard_totalAmount: false,
      expenseDataCard_paymentInfo: false,
    };
  }

  resetBudgetItems() {
    const query = Router.query;

    const brandId = getQueryParamOptional("brandId", query);
    const projectId = getQueryParamOptional("projectId", query);
    const budgetItemId = getQueryParamOptional("budgetItemId", query);
    const autoApprove =
      (this.appStore.workspaceStore.settings
        ?.automatically_approved_expense_items ??
        false) &&
      !!budgetItemId;

    const expenseItemArgs = {
      brand_id: brandId,
      project_id: projectId,
      budget_item_id: budgetItemId,
      budget_item_type: budgetItemId
        ? this.form.getItemTypeFromId(budgetItemId)
        : ExpenseBudgetItemTypeEnum.ExpenseBudgetItem,
      approved: autoApprove,
      responsible_id: autoApprove
        ? (this.appStore.authStore.user?.id ?? undefined)
        : undefined,
    };

    for (const expenseItemKey of Array.from(this.form.expense_items.keys())) {
      this.handleRemoveBudgetItem(expenseItemKey);
    }

    this.form.addExpenseItem(
      new NumberModel(
        0,
        this.appStore.workspaceStore.settings?.currency.denomination ?? 2,
      ),
      expenseItemArgs,
    );
    this.predictionFilledBudgetItems[this.fileCardActiveTab] = false;
  }

  /**
   * Function to clear Contractor and BasicInfo/ExpenseData form
   */
  @action.bound resetForm() {
    const {
      issue_date,
      date_of_supply,
      due_date,
      currency_id,
      total_amount_with_vat_in_currency,
      variable_symbol,
      constant_symbol,
      specific_symbol,
      internal_number,
      document_number,
      note,
      internal_note,
      contract_id,
      contractor_id,
      exchange_rate,
    } = this.form;

    issue_date.reset();
    date_of_supply.reset();
    due_date.reset();
    currency_id.reset(this.appStore.workspaceStore.settings?.currency.id);
    total_amount_with_vat_in_currency.reset();
    variable_symbol.reset();
    constant_symbol.reset();
    specific_symbol.reset();
    internal_number.reset();
    document_number.reset();
    note.reset();
    internal_note.reset();
    contract_id.reset();
    contractor_id.reset();
    exchange_rate.reset();
    this.form.resetExpenseVatItems();

    this.predictionFilledForm[this.fileCardActiveTab] = false;
  }

  @action.bound removePredictionFile() {
    this.predictionFiles[this.fileCardActiveTab] = undefined;
    this.predictionFilledForm[this.fileCardActiveTab] = false;
    this.predictionFilledBudgetItems[this.fileCardActiveTab] = false;
  }

  createVatItem() {
    if (!this.selectedCurrency) return;

    this.form.addExpenseVatItem({
      amount_in_currency: new NumberModel(
        0,
        this.selectedCurrency.denomination,
      ),
      vat_percent_informative:
        this.appStore.workspaceStore.settings?.defaultVatRate.amount!,
    });
    this.syncBudgetItemVatDefaults();
  }

  createBudgetItem() {
    if (!this.selectedCurrency) return;

    const leftAmount =
      this.totalAmountForExpense - this.totalAmountOfBudgetItems;

    this.form.addExpenseItem(
      new NumberModel(leftAmount, this.selectedCurrency.denomination),
      {
        approved: false,
        vatRate:
          this.selectedVatRates.size === 1
            ? (this.vatRates.find(
                ({ amount }) =>
                  amount === this.selectedVatRates.values().next().value,
              ) ?? undefined)
            : undefined,
      },
    );
  }

  @action.bound deselectUnusedVats() {
    for (const item of this.form.expense_items.values()) {
      if (!item.form.$.vatRate.value?.amount) continue;
      if (this.selectedVatRates.has(item.form.$.vatRate.value.amount)) continue;
      item.form.$.vatRate.onChange(undefined);
    }
  }

  /**
   * Set the default value for new/existing budget items.
   *
   * When one VAT rate: set it on all budget items.
   * When many: clear unused.
   */
  @action.bound syncBudgetItemVatDefaults() {
    if (this.selectedVatRates.size === 1) {
      const rateAmount = this.selectedVatRates.values().next().value;
      const matchedRate = this.vatRates.find(
        ({ amount }) => amount === rateAmount,
      );
      if (!matchedRate) return;

      for (const item of this.form.expense_items.values()) {
        if (item.form.$.vatRate.value?.id === matchedRate.id) continue;
        item.form.$.vatRate.onChange(matchedRate);
      }
      return;
    }

    this.deselectUnusedVats();
  }

  @action.bound scaleBudgetItemsToExpenseTotal(
    oldTotal: number,
    newTotal: number,
  ) {
    if (oldTotal === 0 || oldTotal === newTotal) return;

    for (const item of this.form.expense_items.values()) {
      const current = item.form.$.amount_in_currency.value.denominatedValue;
      const scaled = Math.round((current * newTotal) / oldTotal);
      item.form.$.amount_in_currency.onChange(
        item.form.$.amount_in_currency.value.setDenomValue(scaled),
      );
    }
  }

  @action.bound recalculateBudgetItemsVat() {
    for (const item of this.form.expense_items.values()) {
      item.recalculateVatInCurrency();
    }
  }

  @action.bound handleVatItemVatSelect() {
    this.setTotalAmountWithVatInCurrency();
    this.syncBudgetItemVatDefaults();
  }

  setTotalAmountWithVatInCurrency() {
    const vatItems = Array.from(this.form.expense_vat_items.values());
    this.form.total_amount_with_vat_in_currency.onChange(
      this.form.total_amount_with_vat_in_currency.value.setDenomValue(
        vatItems.reduce(
          (curr, { amount_in_currency, vat_in_currency }) =>
            curr +
            amount_in_currency.value.denominatedValue +
            vat_in_currency.value,
          0,
        ),
      ),
    );
    this.convertAmount();
  }

  updateNumberFieldsDenominator() {
    if (!this.selectedCurrency) return;

    const newDenominator = this.selectedCurrency.denomination;

    this.form.expense_vat_items.forEach((i) => {
      i.amount_in_currency.onChange(
        i.amount_in_currency.value.setDenominator(newDenominator),
      );
    });

    this.form.total_amount_with_vat_in_currency.onChange(
      this.form.total_amount_with_vat_in_currency.value.setDenominator(
        newDenominator,
      ),
    );

    this.form.expense_items.forEach((i) => {
      i.form.$.amount_in_currency.onChange(
        i.form.$.amount_in_currency.value.setDenominator(newDenominator),
      );
    });
  }

  @computed get showDateInputForPohoda(): boolean {
    return (
      (this.form.type.value === ExpenseTypeEnum.Bill ||
        this.form.type.value === ExpenseTypeEnum.Card ||
        this.form.type.value === ExpenseTypeEnum.Other ||
        this.form.type.value === ExpenseTypeEnum.PaymentReceipt) &&
      Boolean(this.appStore.workspaceStore.settings?.exportSettings.pohoda)
    );
  }

  canUserAccess(key: IKeys) {
    const { form } = this;
    const RULES: Record<IKeys, () => boolean> = {
      type: () => !this.expense || can("expense_accessAccountingAttributes"),
      contract_id: () =>
        !!form.contractor_id.value &&
        (form.type.value === ExpenseTypeEnum.Contract ||
          form.type.value === ExpenseTypeEnum.Other),
      contractor_id: () =>
        !!form.type.value &&
        form.type.value !== ExpenseTypeEnum.Bill &&
        form.type.value !== ExpenseTypeEnum.Card,
      issue_date: () =>
        form.type.value === ExpenseTypeEnum.Invoice ||
        form.type.value === ExpenseTypeEnum.Proforma ||
        form.type.value === ExpenseTypeEnum.PaymentReceipt ||
        this.showDateInputForPohoda,
      date_of_supply: () =>
        form.type.value === ExpenseTypeEnum.Invoice ||
        form.type.value === ExpenseTypeEnum.Proforma ||
        form.type.value === ExpenseTypeEnum.PaymentReceipt ||
        form.type.value === ExpenseTypeEnum.Contract ||
        form.type.value === ExpenseTypeEnum.Other ||
        this.showDateInputForPohoda,
      due_date: () =>
        form.type.value === ExpenseTypeEnum.Invoice ||
        form.type.value === ExpenseTypeEnum.Proforma ||
        form.type.value === ExpenseTypeEnum.Contract ||
        form.type.value === ExpenseTypeEnum.Other ||
        form.type.value === ExpenseTypeEnum.PaymentReceipt ||
        this.showDateInputForPohoda,
      expense_vat_items: () => can("expense_accessAccountingAttributes"),
      workspace_company_id: () => can("expense_accessAccountingAttributes"),
      workspace_bank_account_id: () =>
        can("expense_accessAccountingAttributes"),
      symbols: () =>
        form.type.value !== ExpenseTypeEnum.Bill &&
        form.type.value !== ExpenseTypeEnum.Card,
      set_accounting: () => can("expense_accessAccountingAttributes"),
      two_percent_fund: () =>
        this.contractor?.business_form === BusinessFormEnum.Person &&
        this.contract?.type === ContractTypeEnum.CopyrightTreaty &&
        form.type.value === ExpenseTypeEnum.Contract,
      expense_no: () => can("expense_accessAccountingAttributes"),
    };
    return RULES[key]();
  }

  inputCreate(opt: {
    is_draft: boolean;
    for_approval: boolean;
  }): CreateExpenseInput {
    let formSerialized = {
      ...this.form.serialize(),
      ...omit(
        this.contractor?.serialize(),
        "id",
        "default_accounting_account_id",
      ),
      is_draft: opt.is_draft,
      for_approval: opt.for_approval,
    };

    if (this.showDateInputForPohoda) {
      return formSerialized;
    }

    switch (this.form.type.value) {
      case ExpenseTypeEnum.Card:
      case ExpenseTypeEnum.Bill:
        return omit(formSerialized, [
          "issue_date",
          "date_of_supply",
          "due_date",
        ]);
      case ExpenseTypeEnum.Other:
      case ExpenseTypeEnum.Invoice:
      case ExpenseTypeEnum.Proforma:
      // biome-ignore lint/suspicious/noFallthroughSwitchClause: Intended fallthrough
      case ExpenseTypeEnum.Contract:
        if (this.contractor) {
          formSerialized = {
            ...formSerialized,
            ...omit(this.contractor.serialize(), "id"),
          };
        }
      default:
        return formSerialized;
    }
  }

  inputUpdate(opt: Parameters<Store["inputCreate"]>[0]): UpdateExpenseInput {
    return {
      id: this.expense?.id!,
      ...omit(this.inputCreate(opt), "fond_contractor_id"),
      ...omit(
        this.contractor?.serialize(),
        "id",
        "default_accounting_account_id",
      ),
    };
  }

  getClientIdFromBrand(brandId?: string): string | undefined {
    const firstExpenseItem = Array.from(this.form.expense_items.values())?.[0];

    if (!firstExpenseItem) return;

    return this.clients.find((client) =>
      client.brands.some(
        (brand) => brand.id === (brandId ?? firstExpenseItem.form.$.brand_id.$),
      ),
    )?.id;
  }

  @action.bound checkIfCurrencyUsesECB(currencyId: string) {
    const matchingCurrency = this.appStore.workspaceStore.currencies.find(
      (currency) => currency.id === currencyId,
    );
    if (matchingCurrency === undefined) return false;
    return matchingCurrency?.exchange_rate === null;
  }

  @action.bound async fetchExchangeRate(
    variables?: Partial<GetExchangeRateQueryVariables>,
    secondTry = false,
  ) {
    if (!this.isUsedDifferentCurrency) return;
    if (!this.selectedCurrency?.id) return;
    const clientIdFromBrand = this.getClientIdFromBrand();
    const clientId =
      variables?.clientId !== null
        ? (variables?.clientId ?? clientIdFromBrand)
        : null;
    const currencyId = variables?.currencyId ?? this.selectedCurrency.id;
    const type =
      variables?.type ??
      (clientId
        ? ExchangeRateSource.Client
        : this.checkIfCurrencyUsesECB(currencyId)
          ? ExchangeRateSource.Auto
          : ExchangeRateSource.Workspace);

    const exchangeRateVariables: GetExchangeRateQueryVariables = {
      date:
        variables?.date ?? toApiDate(this.form.date_of_supply.$ ?? new Date()),
      type,
      clientId,
      currencyId,
      exchangeRate: variables?.exchangeRate,
    };

    const [data, error] = await this.exchangeRateFetcher.fetch(
      exchangeRateVariables,
      undefined,
      type === ExchangeRateSource.Client,
    );

    if (error) {
      if (secondTry) return;
      this.fetchExchangeRate(
        {
          ...exchangeRateVariables,
          type: this.checkIfCurrencyUsesECB(currencyId)
            ? ExchangeRateSource.Auto
            : ExchangeRateSource.Workspace,
        },
        true,
      );
      return;
    }

    this.form.exchange_rate.onChange(
      nominate(
        data.convertAmountToWorkspaceCurrencyUsingExchangeRate?.exchange_rate ??
          1,
        EXCHANGE_RATE_DENOMINATION,
      ),
    );
  }

  @action.bound async convertAmount(
    variables?: Partial<GetExchangeRateQueryVariables>,
    amount = this.totalAmountForExpense,
  ) {
    if (!this.isUsedDifferentCurrency) {
      this.convertedAmount = amount;
      return;
    }
    if (!this.selectedCurrency?.id) return;
    const clientIdFromBrand = this.getClientIdFromBrand();
    const clientId = variables?.clientId ?? clientIdFromBrand;
    const currencyId = variables?.currencyId ?? this.selectedCurrency.id;
    const exchangeRate =
      variables?.exchangeRate ??
      denominate(
        this.form.exchange_rate.value ?? 1,
        EXCHANGE_RATE_DENOMINATION,
      );

    const [data, error] = await this.convertAmountFetcher.fetch(
      {
        date:
          variables?.date ??
          toApiDate(this.form.date_of_supply.value ?? new Date()),
        clientId: clientId,
        type: clientId
          ? ExchangeRateSource.Client
          : this.checkIfCurrencyUsesECB(currencyId)
            ? ExchangeRateSource.Auto
            : ExchangeRateSource.Workspace,
        currencyId: currencyId,
        amount,
        exchangeRate: exchangeRate,
      },
      undefined,
      true,
    );

    if (error) {
      this.convertedAmount = convertToWorkspaceCurrency(amount, {
        exchange_rate: exchangeRate,
      });
      return;
    }

    this.convertedAmount =
      data.convertAmountToWorkspaceCurrencyUsingExchangeRate?.amount;
  }

  @computed get descriptionRows() {
    return this.itemDescriptions
      .filter((description) => !description.deleted_at)
      .map((option) => ({
        id: option.id,
        field: new FieldState(option.title).validators(required),
      }));
  }

  @action.bound handleEditDescription(id: string) {
    this.editingDescriptionIds.add(id);
  }

  @action.bound handleCancelEditDescription(id: string) {
    const row = this.descriptionRows.find((row) => row.id === id);
    if (row) {
      row.field.reset();
    }
    this.editingDescriptionIds.delete(id);
  }

  @action.bound async handleConfirmEditDescription(id: string, title: string) {
    this.updatingDescriptionIds.add(id);
    const [data, error] = await this.expenseItemDescriptionUpdateMutator.mutate(
      {
        id,
        title,
      },
    );
    this.updatingDescriptionIds.delete(id);

    if (error || !data?.updateExpenseItemDescription) {
      return;
    }

    const updatedDescription = data.updateExpenseItemDescription;
    this.itemDescriptions = this.itemDescriptions.map((option) =>
      option.id === id ? updatedDescription : option,
    );

    this.editingDescriptionIds.delete(id);
    this.appStore.UIStore.toast({
      status: "success",
      title: t`Description successfully updated`,
    });
  }

  @action.bound async handleDeleteDescription(id: string) {
    this.updatingDescriptionIds.add(id);
    const [data, error] = await this.expenseItemDescriptionDeleteMutator.mutate(
      {
        id,
      },
    );
    this.updatingDescriptionIds.delete(id);

    if (error || !data?.deleteExpenseItemDescription) {
      return false;
    }

    if (!data?.deleteExpenseItemDescription) {
      this.appStore.UIStore.toast({
        title: t`Error deleting description`,
        status: "error",
      });
      return false;
    }

    this.itemDescriptions = this.itemDescriptions.filter(
      (option) => option.id !== id,
    );

    this.allBudgetItems.forEach((item) => {
      if (item.form.$.description_id.value !== id) return;
      item.form.$.description_id.onChange(undefined);
    });

    this.appStore.UIStore.toast({
      status: "success",
      title: t`Description successfully deleted`,
    });

    return true;
  }

  @computed get isCreatingDescription() {
    return this.expenseItemDescriptionCreateMutator.isLoading.value;
  }

  @action.bound async handleCreateDescription() {
    const validation = await this.newDescriptionField.validate();

    if (validation.hasError) {
      return;
    }

    const [data, error] = await this.expenseItemDescriptionCreateMutator.mutate(
      {
        title: validation.value,
      },
    );

    if (error || !data?.createExpenseItemDescription) return;

    const newDescription = data.createExpenseItemDescription;
    this.itemDescriptions = this.itemDescriptions.concat(newDescription);

    this.newDescriptionField.reset();
    this.appStore.UIStore.toast({
      status: "success",
      title: t`Description successfully created`,
    });
  }

  @computed
  get sequenceType(): SequenceTypesEnum {
    switch (this.form.type.value) {
      case ExpenseTypeEnum.Invoice:
        return SequenceTypesEnum.ExpenseInvoice;
      case ExpenseTypeEnum.Proforma:
        return SequenceTypesEnum.ExpenseProforma;
      case ExpenseTypeEnum.Bill:
        return SequenceTypesEnum.ExpenseBill;
      case ExpenseTypeEnum.Card:
        return SequenceTypesEnum.ExpenseCard;
      case ExpenseTypeEnum.Other:
        return SequenceTypesEnum.ExpenseOther;
      case ExpenseTypeEnum.Contract:
        return SequenceTypesEnum.ExpenseContract;
      case ExpenseTypeEnum.PaymentReceipt:
        return SequenceTypesEnum.ExpensePaymentReceipt;
      default:
        return SequenceTypesEnum.ExpenseAll;
    }
  }

  @action.bound
  handleSelectInvoiceNo(option: IOption, fetchInvoiceNo = true) {
    this.form.expense_no.onChange(option.label);
    this.form.expense_sequence_id.onChange(option.value);
    this.checkInvoiceNumberExist(option.label);
    if (!fetchInvoiceNo) return;
    this.fetchExpenseNo(
      this.sequenceDate,
      this.form.workspace_company_id.value,
      this.sequenceType,
    );
  }

  @action.bound
  handleChangeInvoiceNo(expenseNo: string) {
    this.form.expense_no.onChange(expenseNo);
    if (expenseNo.length > 0) {
      this.checkInvoiceNumberExist(expenseNo);
    } else {
      this.form.expense_sequence_id.onChange(undefined);
      this.form.expense_sequence_id.validate();
    }
  }

  @computed
  get isInvoiceNoFetching() {
    return this.expenseNoFetcher.isLoading.value;
  }

  @action.bound
  async fetchExpenseNo(
    date: Date | string | undefined | null,
    workspaceCompanyId:
      | GetNewSequenceNumberQueryVariables["workspace_company_id"]
      | undefined
      | null,
    invoiceType: GetNewSequenceNumberQueryVariables["invoice_type"],
    preselectInvoiceNo = true,
  ) {
    if (!date || !workspaceCompanyId) return;
    const [data, error] = await this.expenseNoFetcher.fetch({
      date: typeof date === "string" ? date : toApiDate(date),
      workspace_company_id: workspaceCompanyId,
      invoice_type: invoiceType,
      documentId: this.expense?.id,
    });

    if (error) return;

    this.expenseNoOptions = data.getNewInvoiceNumber.map(
      ({ id, document_no }) => ({
        label: document_no,
        value: id,
      }),
    );

    if (!preselectInvoiceNo) return;
    if (data.getNewInvoiceNumber.length !== 1) return;
    this.handleSelectInvoiceNo(this.expenseNoOptions[0], false);
  }

  @action.bound
  handleInvoiceAutocompletePopoverOpen() {
    this.fetchExpenseNo(
      this.sequenceDate,
      this.form.workspace_company_id.value,
      this.sequenceType,
      false,
    );
  }

  @computed
  get noSequenceOptionsMessage(): string | undefined {
    const useDateOfSupply = this.canUserAccess("date_of_supply");
    const date = useDateOfSupply
      ? this.form.date_of_supply.value
      : this.form.userCreatedDate.value;

    if (!date || !this.form.workspace_company_id.value) {
      return t`Please select date of supply and legal entity first.`;
    }

    if (!date) {
      return useDateOfSupply
        ? t`Please select date of supply first.`
        : t`Please select created date first.`;
    }

    return !this.form.workspace_company_id.value
      ? t`Please select legal entity first`
      : undefined;
  }

  @computed
  get sequenceDate(): Date | undefined {
    const useDateOfSupply = this.canUserAccess("date_of_supply");
    return useDateOfSupply
      ? this.form.date_of_supply.value
      : this.form.userCreatedDate.value;
  }

  @computed
  get checkingError() {
    return this.expenseNoCheckFetcher.isLoading.value;
  }

  @action.bound
  async checkInvoiceNumberExist(expenseNo: string): Promise<boolean> {
    if (!this.sequenceDate || !this.form.expense_sequence_id.value)
      return false;

    const [data, error] = await this.expenseNoCheckFetcher.fetch({
      document_no: expenseNo,
      workspace_company_id: this.form.workspace_company_id.value,
      document_id: this.expense?.id,
      invoice_type: this.sequenceType,
      date: toApiDate(this.sequenceDate),
      invoice_order_sequence_id: this.form.expense_sequence_id.value,
    });

    if (error instanceof GraphQLError) {
      this.form.expense_no.setError(
        error?.message.replace("[GraphQL] ", "") ?? "",
      );
      return false;
    }

    if (data?.checkInvoiceNumberExist !== true) return false;
    this.form.expense_no.setError(t`Invoice number already exists.`);
    return true;
  }

  @action.bound
  handleChangeDateOfSupply(
    date: Date | undefined,
    suppyDateEditMode: "change" | "reset" = "change",
  ) {
    if (suppyDateEditMode === "change") {
      this.form.date_of_supply.onChange(date);
    } else {
      this.form.date_of_supply.reset(date);
    }
    this.form.expense_no.onChange(undefined);
    this.form.expense_sequence_id.onChange(undefined);
    this.fetchExpenseNo(
      date,
      this.form.workspace_company_id.value,
      this.sequenceType,
    );
  }

  @action.bound
  handleChangeCreatedDate(date: DateRange | undefined) {
    if (!date) return;
    this.form.userCreatedDate.onChange(date.start);

    if (this.canUserAccess("date_of_supply")) return;

    this.form.expense_no.onChange(undefined);
    this.form.expense_sequence_id.onChange(undefined);
    this.fetchExpenseNo(
      date.start,
      this.form.workspace_company_id.value,
      this.sequenceType,
    );
  }

  @action.bound
  handleChangeLegalEntity(workspaceCompanyId: string) {
    this.form.workspace_company_id.onChange(workspaceCompanyId);
    this.form.workspace_bank_account_id.onChange(undefined);
    this.form.expense_no.onChange(undefined);
    this.form.expense_sequence_id.onChange(undefined);
    this.fetchExpenseNo(
      this.canUserAccess("date_of_supply")
        ? this.form.date_of_supply.value
        : this.form.userCreatedDate.value,
      workspaceCompanyId,
      this.sequenceType,
    );
  }

  taskLabelFormatter(task: TaskOptionLabelFormatterArgs): string {
    return task.name;
  }

  @computed
  get shouldUsePrediction(): boolean {
    return this.predictionFormCheck || this.predictionBudgetItemsCheck;
  }

  @computed
  get isPredictionFetching(): boolean {
    return this.predictionFetcher.isLoading.value;
  }

  @action.bound
  async getPrediction(file: File | undefined) {
    const fileToUse =
      file ??
      this.activePredictionFile ??
      this.form.files[this.fileCardActiveTab].file?._file;

    if (!fileToUse) return;

    const [data, error] = await this.predictionFetcher.fetch({
      file: fileToUse,
    });

    if (error || !data?.fetchOCRExpenseData) return;

    this.ocrPredictions[this.fileCardActiveTab] = data?.fetchOCRExpenseData;

    if (!file) return;

    this.predictionFiles[this.fileCardActiveTab] = file;
  }

  @computed get predictionFormFillInProgress() {
    return (
      (this.isPredictionFetching && this.predictionFormCheck) ||
      this.predictionFillingForm
    );
  }

  @action.bound
  async predictionFillForm(recursive?: boolean) {
    this.predictionFillingForm = true;

    if (!this.prediction) {
      await this.getPrediction(this.activePredictionFile);
      this.predictionFillingForm = false;

      if (recursive) {
        this.appStore.UIStore.toast({
          title: t`There was an error extracting document data.`,
          status: "error",
        });
        return;
      } else {
        this.predictionFillForm(true);
        return;
      }
    }

    this.predictionFilledForm[this.fileCardActiveTab] = true;

    if (this.prediction.issue_date) {
      const date = new Date(this.prediction.issue_date);
      this.form.issue_date.onChange(date);
      this.handleChangeDateOfSupply(date, "reset");
    }
    this.prediction.due_date &&
      this.form.due_date.onChange(new Date(this.prediction.due_date));
    this.form.document_number.onChange(this.prediction.document_number ?? "");
    this.form.variable_symbol.onChange(
      this.prediction.document_number?.replace(/\D/g, "") ?? "",
    );

    if (this.form.currency_id.$ !== this.prediction.currency_id) {
      this.form.currency_id.onChange(this.prediction.currency_id ?? "");
    }

    this.form.resetExpenseVatItems();

    /**
     * @key VAT percent (non-formatted)
     * @value VAT amount
     */
    const vatItems = new Map<number, NumberModel>();
    const denomination =
      this.appStore.workspaceStore.currencies.find(
        ({ id }) => id === this.prediction?.currency_id,
      )?.denomination ?? 2;
    const expenseItemsCount = this.prediction.expense_items?.length ?? 0;

    if (!this.prediction.expense_items?.length) {
      if (this.prediction.total_amount_with_vat_in_currency && denomination) {
        vatItems.set(
          0,
          new NumberModel(
            this.prediction.total_amount_with_vat_in_currency,
            denomination,
          ),
        );
      }
    } else {
      for (const item of this.prediction.expense_items) {
        const vatRate = item.vat_rate ?? 0;
        const currentVatAmount = vatItems.get(vatRate)?.denominatedValue ?? 0;

        vatItems.set(
          vatRate,
          new NumberModel(currentVatAmount + (item.amount ?? 0), denomination),
        );
      }
    }

    for (const [percent, amount] of vatItems) {
      this.form.addExpenseVatItem({
        amount_in_currency: amount,
        vat_percent_informative: percent,
      });
    }

    if (expenseItemsCount > 0) {
      this.setTotalAmountWithVatInCurrency();
    } else {
      if (this.prediction.total_amount_with_vat_in_currency) {
        this.form.total_amount_with_vat_in_currency.onChange(
          new NumberModel(
            this.prediction.total_amount_with_vat_in_currency,
            denomination,
          ),
        );
      }
    }

    this.fileContractor = {
      legal_name: this.prediction.legal_name,
      bankAccounts: [],
    };

    if (this.prediction.contractor_id) {
      const [data, error] = await this.contractorFetcher.fetch({
        id: this.prediction.contractor_id,
      });

      if (data && !error) {
        this.fileContractor.legal_name = data.contractor?.legal_name ?? "";
        this.fileContractor.street = data.contractor?.street ?? "";
        this.fileContractor.city = data.contractor?.city ?? "";
        this.fileContractor.zip = data.contractor?.zip ?? "";
        this.fileContractor.country = data.contractor?.country ?? undefined;
      }
    } else {
      this.fileContractor.legal_name = this.prediction.legal_name ?? "";
      this.fileContractor.street = this.prediction.street ?? "";
      this.fileContractor.city = this.prediction.city ?? "";
      this.fileContractor.zip = this.prediction.zip ?? "";
    }

    if (this.prediction.iban) {
      this.fileContractor.bankAccounts?.push({
        iban: this.prediction.iban,
        swift: this.prediction.swift ?? "",
      });
    }

    this.fileContractor.vat_no = this.prediction.vat_no ?? "";
    this.fileContractor.tax_no = this.prediction.tax_no ?? "";
    this.fileContractor.registration_no = this.prediction.registration_no ?? "";

    this.predictionFillingForm = false;
    this.form.validate();
  }

  @computed get predictionBudgetItemsFillInProgress() {
    return (
      (this.isPredictionFetching && this.predictionBudgetItemsCheck) ||
      this.predictionFillingBudgetItems
    );
  }

  @action.bound
  async predictionFillBudgetItems(recursive?: boolean) {
    this.predictionFillingBudgetItems = true;

    if (!this.prediction) {
      await this.getPrediction(this.activePredictionFile);
      this.predictionFillingBudgetItems = false;

      if (recursive) {
        this.appStore.UIStore.toast({
          title: t`There was an error extracting expense items.`,
          status: "error",
        });
        return;
      } else {
        this.predictionFillBudgetItems(true);
        return;
      }
    }

    this.predictionFilledBudgetItems[this.fileCardActiveTab] = true;

    const query = Router.query;
    const brandId = getQueryParamOptional("brandId", query);
    const projectId = getQueryParamOptional("projectId", query);
    const budgetItemId = getQueryParamOptional("budgetItemId", query);
    const autoApprove =
      (this.appStore.workspaceStore.settings
        ?.automatically_approved_expense_items ??
        false) &&
      !!budgetItemId;
    const denomination =
      this.appStore.workspaceStore.currencies.find(
        ({ id }) => id === this.prediction?.currency_id,
      )?.denomination ?? 2;

    const expenseItemArgs: TAddExpenseItemArgs = {
      brand_id: brandId,
      project_id: projectId,
      budget_item_id: budgetItemId,
      budget_item_type: budgetItemId
        ? this.form.getItemTypeFromId(budgetItemId)
        : ExpenseBudgetItemTypeEnum.ExpenseBudgetItem,
      approved: autoApprove,
      responsible_id: autoApprove
        ? (this.appStore.authStore.user?.id ?? undefined)
        : undefined,
    };

    for (const expenseItemKey of Array.from(this.form.expense_items.keys())) {
      this.handleRemoveBudgetItem(expenseItemKey);
    }

    if (!this.prediction.expense_items?.length) {
      if (this.prediction.total_amount_with_vat_in_currency) {
        this.form.addExpenseItem(
          new NumberModel(
            this.prediction.total_amount_with_vat_in_currency,
            denomination,
          ),
          {
            ...expenseItemArgs,
            vatRate: this.vatRates.find((vat) => vat.amount === 0),
          },
          true,
        );
      }
    } else {
      for (const item of this.prediction.expense_items) {
        this.form.addExpenseItem(
          new NumberModel(item.amount ?? 0, denomination),
          {
            ...expenseItemArgs,
            vatRate: this.vatRates.find((vat) => vat.amount === item.vat_rate),
          },
          true,
        );
      }
    }

    this.predictionFillingBudgetItems = false;
    this.form.validate();
  }

  @computed
  get isLockingExpense(): boolean {
    return this.expense
      ? this.appStore.modelLock.isLocking(
          this.expense?.id,
          ModelLockLockableTypeEnum.Expense,
        )
      : false;
  }

  @computed
  get isUnlockingExpense(): boolean {
    return this.expense
      ? this.appStore.modelLock.isUnlocking(
          this.expense?.id,
          ModelLockLockableTypeEnum.Expense,
        )
      : false;
  }

  @computed
  get isLockExpenseToggling(): boolean {
    return this.isLockingExpense || this.isUnlockingExpense;
  }

  @action.bound
  async lockExpense(): Promise<boolean> {
    if (!this.expense?.id) {
      return false;
    }

    const [data, error] = await this.appStore.modelLock.lockModel(
      this.expense.id,
      ModelLockLockableTypeEnum.Expense,
    );

    if (error || !data?.lockModel) return false;

    this.appStore.UIStore.toast({
      status: "success",
      title: t`Expense locked successfully`,
    });

    this.modelLock = data.lockModel;

    return true;
  }

  @action.bound
  async unlockExpense(): Promise<boolean> {
    if (!this.expense?.id) {
      return false;
    }

    const [data, error] = await this.appStore.modelLock.unlockModel(
      this.expense.id,
      ModelLockLockableTypeEnum.Expense,
    );

    if (error || !data?.unlockModel) return false;

    this.appStore.UIStore.toast({
      status: "success",
      title: t`Expense unlocked successfully`,
    });

    this.modelLock = null;

    return true;
  }

  @computed
  get arePaymentMethodInputsDisabled(): boolean {
    if (can("expense_markAsPaid")) return false;
    return this.mode !== FormMode.CREATE;
  }

  @computed
  get totalRemainingAmount(): number {
    return this.totalAmountForExpense - this.totalAmountOfBudgetItems;
  }
}
